/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslate } from '@im/react-localization';
import { useTheme } from '@mui/system';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useAlert } from 'src/hooks/useAlert';
import { isEmbedded } from 'src/util';
import {
  ProgramAttributesType,
  RuleSubmitRequest,
  useRuleSubmit,
} from '../../hooks/rule-submit';
import {
  SingleRuleForm,
  useSingleRuleFrom,
} from '../../hooks/single-rule-form-data';
import { RuleEditRequest, useRuleEdit } from '../../hooks/rule-edit';
import PricingDatePicker from '../components/PricingDatePicker';
import PricingStrategy from '../components/PricingMarginStrategy';
import BusinessJustification from '../components/BusinessJustification';
import PricingMargin from '../components/PricingMargin';
import { PriorityChange } from '../components/PriorityChange';
import ProgramAttributes from '../components/ProgramAttributes';
import CommentAdd from '../components/CommentAdd';
import CustomerSearchBar from '../components/CustomerSearchBar';
import VendorSearchBar from '../components/VendorSearchBar';
import { useSeachQueryParams } from '../../hooks/singlePricingSearch';
import dayjs from 'dayjs';
import { AutoDenial } from '../components/AutoDenial';

export function SingleRuleTab(
  props: Readonly<{
    initialFormData?: SingleRuleForm;
    isEdit?: boolean;
  }>
) {
  const showAlert = useAlert();
  const [searchParams] = useSearchParams();
  const translate = useTranslate();
  const companyCode = searchParams.get('companyCode')?.toUpperCase() ?? 'MD';
  const countryCode = searchParams.get('countryCode')?.toUpperCase() ?? 'US';
  const [nchecked, setNchecked] = useState(false);
  const [isDenialchecked, setIsDenialchecked] = useState(false);
  const submitRule = useRuleSubmit();
  const editRule = useRuleEdit();
  const { formData, setFormData, validateFormData, validateMarginStrategy } =
    useSingleRuleFrom(companyCode, countryCode, props.initialFormData);

  function clearForm() {
    setFormData(new SingleRuleForm());
    setCustomerFilter({
      field: 'customer',
      value: '',
    });
    setVendorFilter({
      field: 'vendor',
      value: '',
    });
    setIsDenialchecked(false);
  }
  function handleCancel() {
    showAlert(null);
    clearForm();
  }
  const handleComment = (c: any) => {
    setFormData({ comment: c });
  };
  const handleChangePriority = () => {
    setFormData({
      requestPriority: nchecked ? 'STANDARD' : 'URGENT',
    });
    setNchecked(!nchecked);
  };
  const handleSupressDenial = () => {
    setFormData({
      supressAutoDenial: !isDenialchecked,
    });
    setIsDenialchecked(!isDenialchecked);
  };

  const handleJustification = (j: any) => {
    setFormData({ autoDenialJustification: j });
  };

  const [disableSubmit, setDisableSubmit] = useState(false);
  const setExpirationDate = (value: any) => {
    if (
      formData.effectiveDate.month() < 9 &&
      value.isAfter(formData.effectiveDate.add(1, 'year'))
    ) {
      setFormData({
        ...formData,
        expirationDate: value,
        expirationDateError: translate(
          'Pricing.InvalidExpirationDateErrorQ1toQ3'
        ),
      });
      setDisableSubmit(true);
    } else if (
      formData.effectiveDate.month() >= 9 &&
      value.isAfter(dayjs(`${formData.effectiveDate.year() + 2}-01-01`))
    ) {
      setFormData({
        ...formData,
        expirationDate: value,
        expirationDateError: translate('Pricing.InvalidExpirationDateErrorQ4'),
      });
      setDisableSubmit(true);
    } else {
      setFormData({
        ...formData,
        expirationDate: value,
        expirationDateError: undefined,
      });
      setDisableSubmit(false);
    }
  };
  const setEffectiveDate = (value: any) => {
    if (
      value.month() < 9 &&
      formData.expirationDate.isAfter(value.add(1, 'year'))
    ) {
      setFormData({
        ...formData,
        effectiveDate: value,
        expirationDateError: translate(
          'Pricing.InvalidExpirationDateErrorQ1toQ3'
        ),
      });
      setDisableSubmit(true);
    } else if (
      value.month() >= 9 &&
      formData.expirationDate.isAfter(dayjs(`${value.year() + 2}-01-01`))
    ) {
      setFormData({
        ...formData,
        effectiveDate: value,
        expirationDateError: translate('Pricing.InvalidExpirationDateErrorQ4'),
      });
      setDisableSubmit(true);
    } else {
      setFormData({
        ...formData,
        effectiveDate: value,
        expirationDateError: undefined,
      });
      setDisableSubmit(false);
    }
  };
  function handelSubmit() {
    if (!validateFormData(formData)) return;
    const EfflocalDate1 = new Date(
      formData.effectiveDate.toISOString()
    ).toLocaleString();
    const EfflocalDate = new Date(EfflocalDate1);
    const ExplocalDate1 = new Date(
      formData.expirationDate.toISOString()
    ).toLocaleString();
    const ExplocalDate = new Date(ExplocalDate1);
    const req: RuleSubmitRequest = {
      autoDenialJustification: formData.autoDenialJustification ?? '',
      bussinessJustificationId: formData.businessJustification,
      comment: formData.comment ?? '',
      countryCode: countryCode ?? '',
      customerBranchNumber: formData.customer.customerBranchNumber,
      customerCompanyCode: formData.customer.customerCompanyCode,
      customerNumber: formData.customer.customerNumber,
      effectiveDate: formData.effectiveDate?.isValid()
        ? `${EfflocalDate.getFullYear()}-${(EfflocalDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${EfflocalDate.getDate()
            .toString()
            .padStart(2, '0')}T00:00:00.000Z`
        : '',
      expirationDate: formData.expirationDate?.isValid()
        ? `${ExplocalDate.getFullYear()}-${(ExplocalDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${ExplocalDate.getDate()
            .toString()
            .padStart(2, '0')}T00:00:00.000Z`
        : '',
      marginStrategy: formData.marginStrategy ?? 'REPLACEMENT_COST',
      modPercent: formData.marginDelta ?? 0,
      ...(formData.programAttributes && {
        programAttributes: formData.programAttributes,
      }),
      requestPriority: formData.requestPriority ?? 'STANDARD',
      requestProcessingType: 'VALIDATION',
      segmentId: formData.customer.segmentId ?? null,
      supressAutoDenial: formData.supressAutoDenial ?? false,
      vendorCompanyCode: formData.vendor.vendorCompanyCode,
      vendorNumber: formData.vendor.vendorCode,
    };
    if (props.isEdit) {
      editRule
        .mutateAsync(req as unknown as RuleEditRequest)
        .then(() => {
          showAlert({
            text: translate('Pricing.Submitted'),
            type: 'success',
          });
          clearForm();
        })
        .catch((e: Error) => showAlert({ text: e.message, type: 'error' }));
    } else {
      submitRule
        .mutateAsync(req)
        .then(() => {
          showAlert({
            text: translate('Pricing.Submitted'),
            type: 'success',
          });
          clearForm();
        })
        .catch((e: Error) => showAlert({ text: e.message, type: 'error' }));
    }
  }
  const theme = useTheme();
  const { searchQuery: searchCustomerQuery, setFilter: setCustomerFilter } =
    useSeachQueryParams({
      initialFilterField: 'customer',
      page: 0,
      size: 10,
    });
  const { searchQuery: searchVendorQuery, setFilter: setVendorFilter } =
    useSeachQueryParams({
      initialFilterField: 'vendor',
      page: 0,
      size: 10,
    });
  return (
    <Box pt={2}>
      <Stack direction="column" pb={8}>
        <Stack
          display="grid"
          gridTemplateColumns="1fr 1fr"
          sx={{ gridGap: '24px' }}
        >
          <CustomerSearchBar
            onValueSelected={(customer: any) => setFormData({ customer })}
            searchQuery={searchCustomerQuery}
            setFilter={setCustomerFilter}
            value={formData.customer}
          />
          <VendorSearchBar
            onValueSelected={(vendor: any) => setFormData({ vendor })}
            searchQuery={searchVendorQuery}
            setFilter={setVendorFilter}
            value={formData.vendor}
          />
        </Stack>
        <Divider
          sx={{
            borderColor: theme.palette.neutral[200],
            marginBottom: `8px`,
            marginTop: `20px`,
          }}
        />
        <Stack direction="column" flex={1}>
          {searchVendorQuery?.filter?.field === 'program' && (
            <>
              <Typography fontWeight={'bold'} mb={1} mt={2} variant="body2">
                {translate('Pricing.SelectProgramAttributes')}
              </Typography>
              <ProgramAttributes
                onChange={(programAttributes: ProgramAttributesType) => {
                  setFormData({ programAttributes });
                }}
                program={formData.vendor}
              />
            </>
          )}
          <Typography fontWeight={'bold'} mb={1} mt={2} variant="body2">
            {translate('Pricing.SetPricingRule')}
          </Typography>
          <PricingStrategy
            error={formData.marginStrategyError}
            onChange={(e, value) => {
              setFormData({
                marginStrategy: value,
                marginStrategyError: undefined,
              });
              validateMarginStrategy({
                marginDelta: formData.marginDelta,
                marginStrategy: value,
              });
            }}
            value={formData.marginStrategy}
          />
          <PricingMargin
            error={formData.marginDeltaError}
            onChange={(value: any) => {
              setFormData({
                marginDelta: value,
                marginDeltaError: undefined,
              });
              validateMarginStrategy({
                marginDelta: value,
                marginStrategy: formData.marginStrategy,
              });
            }}
            value={formData.marginDelta}
          />
          <Typography fontWeight={'bold'} mb={1} mt={3} variant="body2">
            {translate('Pricing.BusinessJustification')}
          </Typography>
          <BusinessJustification
            countryCode={countryCode ?? ''}
            error={formData.businessJustificationError}
            onChange={(v) => setFormData({ businessJustification: v })}
            showAlert={showAlert}
            value={formData.businessJustification}
          />
          <PricingDatePicker
            effectiveDate={formData.effectiveDate}
            effectiveDateError={formData.effectiveDateError}
            expirationDate={formData.expirationDate}
            expirationDateError={formData.expirationDateError}
            setEffectiveDate={setEffectiveDate}
            setExpirationDate={setExpirationDate}
          />
          <PriorityChange
            handleChangePriority={handleChangePriority}
            nchecked={nchecked}
          />
          <AutoDenial
            error={formData.autoDenialJustificationError}
            handleJustification={handleJustification}
            handleSupressDenial={handleSupressDenial}
            isDenialchecked={isDenialchecked}
            justification={formData.autoDenialJustification}
          />
          <CommentAdd handleComment={handleComment} />
        </Stack>
      </Stack>
      <Stack
        bgcolor="white"
        bottom={0}
        boxShadow={'0px 0px 16px 0px #184B9D3D'}
        direction={'row'}
        gap={2}
        justifyContent={'end'}
        left={0}
        p={2}
        pb={isEmbedded() ? 0 : 2}
        position="fixed"
        width="100%"
      >
        <Button color="info" onClick={handleCancel} variant="newOutlined">
          {translate('Common.Cancel')}
        </Button>
        <Button
          disabled={disableSubmit}
          onClick={handelSubmit}
          variant="newContained"
        >
          {translate('Common.Submit')}
        </Button>
      </Stack>
    </Box>
  );
}
